// src/services/standardBlockAdvice.js
//
// SourceX — "there's a standard block for this".
//
// A handful of sections recur in every study's source document: adverse events,
// concomitant medications, exacerbations, physical exam, medical/surgical
// history. RegX ships a standard, agreed wording for each — under "Use from
// template" for the ones that live inside a visit, and "Add Log" for the
// standing paper logs.
//
// They are also the sections coordinators most often build by hand, or that an
// AI-generated template drafts in its own words. Every hand-built version is a
// different form asking the same question, which is exactly what a monitor
// finds and what a standardized site is supposed to prevent.
//
// So: this module reads a visit's questions and reports which of its sections
// have a standard equivalent that ISN'T being used. The editor and the outline
// highlight those, with one line naming where the standard lives. It is advice,
// never a block — nothing here prevents an edit, an approval or an export.
//
// PURE AND DATA-ONLY. No Firebase, no React, no PHI: it takes an array of
// questions and returns a Map. That is what lets it be tested with plain
// objects, and what keeps "which sections are standardized" one decision in one
// file rather than a condition scattered through the editor.
//
// HOW A SECTION IS RECOGNISED AS ALREADY-STANDARD
//
// Not by a flag on the document. A stamp would have to be added to the schema,
// would only ever be true for blocks inserted AFTER the stamp existed, and
// would therefore nag every coordinator who did the right thing last month.
//
// Instead, by CONTENT: a section that contains every question of the standard
// block, word for word, IS the standard block — whoever inserted it and
// whenever. Containment rather than equality, so adding a study-specific
// question underneath a standard block doesn't make it non-standard. Nothing a
// coordinator writes by hand reproduces a 130-character standard question
// verbatim, so this does not produce false exemptions either.

import { SOURCE_QUESTION_TEMPLATES } from "./sourceQuestionTemplates";
import { SOURCE_LOG_TEMPLATES } from "./sourceLogTemplates";

// The menu each standard block is reached from — the words on the button, so
// the advice names something the coordinator can actually go and click.
export const FROM_TEMPLATE_MENU = "Use from template";
export const ADD_LOG_MENU = "Add Log";

/**
 * The standardized topics, and which catalog entries cover each one.
 *
 * The keyword lists are declared here rather than derived from the block labels
 * because they are not the same thing: the block is called "Concomitant
 * Medications" and coordinators write "Con Meds". Which words mean a topic is a
 * judgement, and it belongs in one readable list.
 *
 * `sources` points at catalog entries by id, so a block that is renamed or
 * withdrawn changes this file's behaviour without this file being edited — an
 * unavailable block is dropped from the advice below, because sending someone
 * to a greyed-out menu row is worse than saying nothing.
 *
 * @typedef {object} StandardTopic
 * @property {string} id
 * @property {string} name      what to call the topic in one or two words
 * @property {string[]} keywords  phrases that mean this topic
 * @property {Array<{menu: string, id: string}>} sources  catalog entries
 */
/** @type {StandardTopic[]} */
export const STANDARD_TOPICS = [
  {
    id: "concomitant-medications",
    name: "Concomitant Medications",
    keywords: [
      "concomitant medication",
      "concomitant med",
      "con med",
      "conmed",
      "comed",
    ],
    sources: [
      { menu: FROM_TEMPLATE_MENU, id: "concomitant-medications" },
      { menu: ADD_LOG_MENU, id: "con-med-log" },
    ],
  },
  {
    id: "adverse-events",
    name: "Adverse Events",
    keywords: [
      "adverse event",
      "adverse experience",
      "adverse reaction",
      "serious adverse event",
      // The abbreviations, because that is what most people actually type
      // ("New AEs?", "SAE reporting"). Safe as keywords only because the match
      // is word-bounded: "ae" is found as its own word, never inside another.
      "ae",
      "sae",
    ],
    sources: [
      { menu: FROM_TEMPLATE_MENU, id: "adverse-events" },
      { menu: ADD_LOG_MENU, id: "ae-log" },
    ],
  },
  {
    id: "exacerbations",
    name: "Exacerbations",
    keywords: ["exacerbation"],
    sources: [
      { menu: FROM_TEMPLATE_MENU, id: "exacerbations" },
      { menu: ADD_LOG_MENU, id: "exacerbation-log" },
    ],
  },
  {
    id: "physical-exam",
    name: "Physical Exam",
    keywords: ["physical exam", "physical examination", "pe assessment"],
    sources: [{ menu: FROM_TEMPLATE_MENU, id: "physical-exam" }],
  },
  {
    id: "medical-surgical-history",
    name: "Medical/Surgical History",
    keywords: [
      "medical history",
      "surgical history",
      "medical surgical history",
      "med surg history",
      "past medical history",
    ],
    sources: [{ menu: ADD_LOG_MENU, id: "medical-surgical-history-log" }],
  },
  {
    id: "hospitalizations",
    name: "Hospitalizations",
    keywords: ["hospitalization", "hospitalisation", "hospital admission"],
    sources: [{ menu: FROM_TEMPLATE_MENU, id: "hospitalization-log" }],
  },
];

/**
 * Text reduced to the form the keyword lists are written in: lower case, with
 * the separators people vary on ("con-med", "medical/surgical") flattened to
 * spaces, so one keyword covers every way of writing it.
 */
const normalize = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[/\-–—_]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Whole-word match, with an optional plural on the last word — "exacerbation"
// finds "Exacerbations", "concomitant medication" finds "concomitant
// medications". Substring matching without the word boundary would find
// "pe assessment" inside "type assessment".
const keywordPattern = (keyword) =>
  new RegExp(`\\b${normalize(keyword).replace(/ /g, "\\s+")}s?\\b`);

// Built once. Rebuilding a dozen RegExps per question per render, on a document
// that re-renders on every keystroke, is the kind of cost that only shows up on
// somebody's laptop with a 200-question template open.
const TOPIC_PATTERNS = STANDARD_TOPICS.map((topic) => ({
  topic,
  patterns: topic.keywords.map(keywordPattern),
}));

/**
 * Which standardized topic (if any) a piece of text is about.
 *
 * @param {string} text  a heading or question label
 * @returns {StandardTopic|null}
 */
export const findStandardTopic = (text) => {
  const normalized = normalize(text);
  if (!normalized) return null;
  const hit = TOPIC_PATTERNS.find(({ patterns }) =>
    patterns.some((p) => p.test(normalized))
  );
  return hit ? hit.topic : null;
};

// The catalog entry behind one `sources` reference, or undefined.
const catalogEntry = ({ menu, id }) =>
  (menu === ADD_LOG_MENU ? SOURCE_LOG_TEMPLATES : SOURCE_QUESTION_TEMPLATES).find(
    (t) => t.id === id
  );

/**
 * Where a topic's standard lives — EVERY block for it, including withdrawn
 * ones, each marked with whether it can be used yet.
 *
 * Withdrawn blocks used to be dropped here, on the reasoning that pointing
 * someone at a greyed-out menu row is not pointing them anywhere. But several
 * topics are standardized in BOTH menus — a Concomitant Medications prompt
 * inside the visit and a Concomitant Medication Log for the whole study — and
 * hiding the withdrawn half meant a coordinator was told about one and never
 * learned the other existed at all. Knowing a standard is coming is exactly
 * what stops someone building their own version of it this afternoon.
 *
 * So both are listed, and `available` says which can be clicked today. Order
 * follows `sources`, which puts the visit block before the standing log.
 *
 * @param {StandardTopic} topic
 * @returns {Array<{label: string, available: boolean}>}
 */
export const standardSourcesFor = (topic) =>
  (topic?.sources || [])
    .map((source) => {
      const entry = catalogEntry(source);
      return entry
        ? { label: `${source.menu} → ${entry.label}`, available: !entry.unavailable }
        : null;
    })
    .filter(Boolean);

/** Every question the standard blocks for a topic are made of, normalized. */
const standardQuestionTexts = (topic) => {
  const out = [];
  (topic.sources || []).forEach((source) => {
    const entry = catalogEntry(source);
    (entry?.questions || []).forEach((q) => {
      const text = normalize(q.question);
      if (text) out.push({ sourceId: entry.id, text });
    });
  });
  return out;
};

/**
 * Is this group of questions already one of the topic's standard blocks?
 *
 * True when the group contains every question of at least ONE of the topic's
 * blocks, verbatim. See the note at the top of the file for why containment
 * rather than a flag, and why containment rather than equality.
 *
 * A block with no questions of its own (a placeholder for a log still being
 * written) can never be matched this way — an empty list would otherwise be
 * "contained" by every group and exempt the whole topic.
 */
const groupIsStandard = (topic, questions) => {
  const present = new Set(questions.map((q) => normalize(q.question)));
  const bySource = new Map();
  standardQuestionTexts(topic).forEach(({ sourceId, text }) => {
    if (!bySource.has(sourceId)) bySource.set(sourceId, []);
    bySource.get(sourceId).push(text);
  });
  for (const texts of bySource.values()) {
    if (texts.length && texts.every((t) => present.has(t))) return true;
  }
  return false;
};

/**
 * Split a visit's questions into heading groups, exactly as the document and
 * the outline draw them: a `Header` starts a group, everything after it belongs
 * to it, and anything before the first heading is its own leading group.
 */
const groupByHeading = (questions) => {
  const groups = [];
  let current = { heading: null, items: [] };
  questions.forEach((q) => {
    if (q?.type === "Header") {
      if (current.heading || current.items.length) groups.push(current);
      current = { heading: q, items: [] };
    } else if (q) {
      current.items.push(q);
    }
  });
  if (current.heading || current.items.length) groups.push(current);
  return groups;
};

/**
 * @typedef {object} StandardAdvice
 * @property {string} topicId
 * @property {string} name     the topic, in one or two words
 * @property {Array<{label: string, available: boolean}>} where  every block for
 *   this topic — "Use from template → Adverse Events", usable or not yet
 * @property {string[]} useNow  the labels that can be clicked today
 * @property {string[]} coming  the labels still being written
 * @property {string} headingId  the row the advice is anchored to
 * @property {string[]} questionIds  every question in the flagged section
 */

/**
 * Which of a visit's sections have a standard block that isn't being used.
 *
 * One advice per SECTION, not per question: the standard block is a section, so
 * a section is what gets replaced, and flagging each of its questions
 * separately would put the same sentence on screen five times.
 *
 * @param {Array<object>} questions  one visit's questions, in document order
 * @returns {Map<string, StandardAdvice>} keyed by the id of every row that
 *   should be highlighted — the heading AND each question under it — so a
 *   renderer can ask about one row without re-deriving the grouping. Every
 *   entry of one section points at the SAME advice object, whose `headingId`
 *   says which row carries the written warning.
 */
export const buildStandardAdvice = (questions = []) => {
  const advice = new Map();
  groupByHeading(questions).forEach((group) => {
    const rows = [...(group.heading ? [group.heading] : []), ...group.items];
    if (!rows.length) return;

    // EVERY standardized topic this section touches, not just the first. A
    // "Safety" section can carry a hand-built AE question and a hand-built
    // con-med one; if only the first were considered, replacing it with the
    // standard block would make the whole section count as standard and the
    // second would never be raised at all.
    //
    // The heading goes first, because it names the section — a section titled
    // "Adverse Events" is about adverse events whatever its questions say.
    const topics = [];
    [group.heading, ...group.items].forEach((row) => {
      const found = findStandardTopic(row?.question);
      if (found && !topics.includes(found)) topics.push(found);
    });

    // Topics already covered by the standard block itself drop out. So does a
    // topic with nothing usable yet: a notice raised before there is anything
    // to click is one a coordinator can only dismiss, and an advisory whose
    // only available response is "go away" trains people to stop reading the
    // colour. A topic whose block is still being written stays silent until it
    // ships — at which point it starts flagging on its own, with no change
    // here.
    //
    // A withdrawn block is still NAMED once its topic qualifies on some other
    // block (see standardSourcesFor) — that is how a coordinator learns both
    // homes exist. It just can't be the reason a notice appears.
    //
    // Whatever is left, the FIRST of them is what gets said. One sentence per
    // section: fix it and the next render raises the next one, which keeps the
    // warning short and keeps the section from growing a list of complaints.
    const topic = topics.find(
      (t) =>
        !groupIsStandard(t, group.items) &&
        standardSourcesFor(t).some((s) => s.available)
    );
    if (!topic) return;
    const where = standardSourcesFor(topic);

    const entry = {
      topicId: topic.id,
      name: topic.name,
      where,
      // Split out so a renderer doesn't re-derive it: what to use NOW, and
      // what to expect later. A topic with nothing usable yet reads as a
      // heads-up rather than as an instruction that can't be followed.
      useNow: where.filter((w) => w.available).map((w) => w.label),
      coming: where.filter((w) => !w.available).map((w) => w.label),
      // With no heading, the first row of the group carries the warning.
      headingId: (group.heading || rows[0]).id,
      questionIds: rows.map((q) => q.id),
    };
    rows.forEach((q) => advice.set(q.id, entry));
  });
  return advice;
};

/**
 * The key one dismissal is remembered under.
 *
 * The row AND the topic, not the row alone. A coordinator dismissing the notice
 * on "Safety" is saying "this study keeps its own adverse-events wording" —
 * they are not signing off on whatever that section might be about after
 * somebody renames it. Renaming the heading to "Con Meds" therefore raises the
 * con-med notice as if it had never been dismissed, which is right: it is a
 * different decision and nobody has taken it.
 *
 * Plain `[A-Za-z0-9_-]`, doubled underscore as the separator, because this
 * becomes a key in a Firestore map.
 *
 * @param {StandardAdvice} advice
 * @returns {string}
 */
export const standardAdviceKey = (advice) =>
  advice ? `${advice.headingId}__${advice.topicId}` : "";

/**
 * Has this notice been dismissed?
 *
 * @param {object} [dismissed]  the template's `standardAdviceDismissed` map
 * @param {StandardAdvice} [advice]
 * @returns {boolean}
 */
export const isStandardAdviceDismissed = (dismissed, advice) =>
  !!advice && !!dismissed && dismissed[standardAdviceKey(advice)] === true;

/**
 * The whole warning in one sentence, for a tooltip or a screen reader.
 *
 * Both halves are named. A topic standardized in both menus has two homes and a
 * coordinator needs to know about both, not just the one they can click today.
 * `useNow` is never empty here — buildStandardAdvice does not raise a notice
 * until a topic has something usable.
 *
 * @param {StandardAdvice} entry
 * @returns {string}
 */
export const describeStandardAdvice = (entry) => {
  if (!entry) return "";
  const coming = entry.coming.length
    ? ` Also coming: ${entry.coming.join(" and ")}.`
    : "";
  return `${entry.name} has a standard block — ${entry.useNow.join(
    " or "
  )}. Use it so every study matches.${coming}`;
};
